import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { patchState, signalState } from '@ngrx/signals';
import { PublicLinkApiService } from '@simOn/public-link/data-access';
import {
  EMPTY_PUBLIC_LINK_CAPABILITIES,
  legacyPublicLinkCapabilities,
  ParsedJwtPublicLink,
  PublicLinkAccess,
  PublicLinkCapabilities,
  PublicLinkCapability,
  PublicLinkData,
  PublicLinkDetailsDto,
  PublicLinkSession,
  PublicLinkStartingScreen,
  toPublicLinkStartingScreen
} from '@simOn/public-link/models';
import { API_URL } from '@simOn/utils/tokens';
import { BehaviorSubject, Observable, catchError, defer, map, of, shareReplay, tap } from 'rxjs';

/** sessionStorage key of a JWT public link session (legacy links use 'publicLinkParams'). */
const JWT_SESSION_STORAGE_KEY = 'publicLinkSession';

export type PublicLinkCredentialHeader = { name: 'publicLinkToken' | 'PublicLink'; value: string };

const API_BLACK_LIST = [
  `/Access/GetAllUsersRoles`,
  `/Access/GetApartmentRoles`,
  `/ApartmentInvitations/GetApartmentInvitations`,
  `/Tickets/MarkTicketAsRead`,
  `/Paywall/GetApartmentLimitsInfo`,
  `/Apartments/GetApartmentOwnerInfo`,
  `/Matterport/GetProviderConnectionStatus`,
  `/NavVis/GetProviderConnectionStatus`,
  `/Maximo/GetProviderConnectionStatus`,
  `/InteriorDesignerLibrary/GetPublicModels`,
  `/Tickets/MarkTicketAsRead`,
  `/MaximoLink/GetAuthors`,
  `/MaximoLink/GetResources`
];

@Injectable({
  providedIn: 'root'
})
export class PublicLinkService {
  private readonly _http = inject(HttpClient);
  private readonly _apiUrl = inject<string>(API_URL);
  private readonly _publicLinkApi = inject(PublicLinkApiService);
  private readonly _jwtState = signalState<{ session: PublicLinkSession | null }>({ session: null });
  private _validation$: Observable<boolean> | null = null;
  /** Details restored from sessionStorage are shown at once but still re-validated once per page load. */
  private _validatedThisLoad = false;

  private router = inject(Router);
  private _publicLink$ = new BehaviorSubject<string | null>(null);
  private readonly _publicLinkActive = toSignal(this._publicLink$, { initialValue: null });
  /** Whether the legacy link carries an `interior` config; feeds `canViewInteriorTemplates` for legacy links. */
  private readonly _legacyInterior = signal(false);
  private _publicLinkInteriorConfig: string | undefined;

  /** JWT public link session (new links); `null` for legacy links and logged users. */
  readonly session = this._jwtState.session;
  readonly isJwtSession = computed(() => !!this.session());
  /**
   * Capabilities of the active link. JWT: empty until `ValidatePublicLink` answered. Legacy: today's fixed rules
   * (`legacyPublicLinkCapabilities`). Logged user: empty - use `allows()` to ask what a visitor may do.
   */
  readonly capabilities = computed<PublicLinkCapabilities>(() => {
    const session = this.session();
    if (session) return session.details ? pickCapabilities(session.details) : EMPTY_PUBLIC_LINK_CAPABILITIES;
    return this._publicLinkActive()
      ? legacyPublicLinkCapabilities(this._legacyInterior())
      : EMPTY_PUBLIC_LINK_CAPABILITIES;
  });

  public publicLink$ = this._publicLink$.asObservable();
  public publicLink = this._publicLink$.value;
  public publicLinkStartingScreen: PublicLinkStartingScreen = PublicLinkStartingScreen.widgets;
  get publicLinkInteriorConfig(): string | undefined {
    return this._publicLinkInteriorConfig;
  }
  set publicLinkInteriorConfig(value: string | undefined) {
    this._publicLinkInteriorConfig = value;
    this._legacyInterior.set(!!value);
  }
  public publicLinkHideMenu: boolean = false;
  public publicLinkHideExitLabel: boolean = false;

  publicLinkUrl = this._publicLink$.value;
  readonly isPublicLinkModeActive$: Observable<boolean> = this._publicLink$.pipe(
    map((publicLink: string | null) => !!publicLink)
  );
  public apartmentId: string | null = null;

  constructor() {
    if (!window.location.href.includes('public')) {
      const publicLink = sessionStorage.getItem('publicLinkParams');
      if (!publicLink) {
        this._restoreJwtSession();
        return;
      }
      sessionStorage.removeItem(JWT_SESSION_STORAGE_KEY);
      const params: PublicLinkData = JSON.parse(publicLink);
      if (params) {
        this.publicLinkHideMenu = params.plhidemenu;
        this.publicLinkHideExitLabel = params.plhideexitlabel;
        this.publicLink = params.publicLink;
        this.publicLinkStartingScreen = params.startingScreen;
        this.setGuid(params.publicLink);
        this.goTo(params);
      }
    }
  }

  get publicLinkFromSessionStorage(): PublicLinkData {
    const publicLinkParams = sessionStorage.getItem('publicLinkParams') || '';
    return JSON.parse(publicLinkParams);
  }

  goTo(params: PublicLinkData) {
    const { url } = params;
    const apartmentId = url.split('id=')[1];
    const _url = url.split('?id=')[0];
    let queryParams = {};
    url
      .split('?')[1]
      .split('&')
      .forEach((param) => {
        const key = param.split('=')[0];
        const value = param.split('=')[1];
        queryParams = { ...queryParams, [key]: value };
      });
    setTimeout(() => {
      this.router.navigate([`space/${apartmentId}/${_url}`], { queryParams: queryParams });
    }, 0);
  }

  setSelectedApartmentId(apartmentId: string, local: boolean = true): void {
    this.apartmentId = apartmentId;
    // if (local) return;
    // throw 'NOT IMPLEMENTED!!!';

    // this.applicationFacade.setSelectedApartmentId(apartmentId, false);
  }
  selectGuid$(): Observable<string | null> {
    return this._publicLink$.asObservable();
  }

  getGuid(): string | null {
    return this._publicLink$.value;
  }
  setGuid(publicLink: string | null) {
    this._publicLink$.next(publicLink);
  }

  /**
   * Leaves public link mode. `keepJwtSession` is only for app startup, which resets the legacy link
   * but must keep a JWT session alive across page refreshes.
   */
  removeGuid(options: { keepJwtSession?: boolean } = {}): void {
    if (this._publicLink$.value !== null) {
      this._publicLink$.next(null);
    }
    this.publicLink = null;
    this.apartmentId = null;

    sessionStorage.removeItem('publicLinkParams');

    const session = this.session();
    if (options.keepJwtSession) {
      if (session) this._applyJwtLink(session.link);
      return;
    }
    // Also clears a JWT session left in storage while a legacy link was in use in this tab.
    this.endSession();
  }

  /** Starts a JWT public link visit; capabilities stay empty until `setDetails`. */
  startSession(link: ParsedJwtPublicLink): void {
    this._validation$ = null;
    this._validatedThisLoad = false;
    patchState(this._jwtState, { session: { link, details: null } });
    this._persistJwtSession();
    this._applyJwtLink(link);
  }

  /** Stores the `ValidatePublicLink` answer for the current JWT session. */
  setDetails(details: PublicLinkDetailsDto): void {
    const session = this.session();
    if (!session) return;
    patchState(this._jwtState, { session: { ...session, details } });
    this._persistJwtSession();
    this.publicLinkStartingScreen = toPublicLinkStartingScreen(details.startingScreen);
  }

  /**
   * Validates the JWT session once per page load (cached), so a link revoked or edited by a manager is noticed
   * after a refresh. A 401 ends the session; a network or server error keeps a session restored with details.
   */
  ensureValidated$(): Observable<boolean> {
    return defer(() => {
      const session = this.session();
      if (!session) return of(false);
      if (session.details && this._validatedThisLoad) return of(true);
      this._validation$ ??= this._publicLinkApi.validate$().pipe(
        tap((details) => {
          this._validatedThisLoad = true;
          this.setDetails(details);
        }),
        map(() => true),
        catchError((error: { status?: number }) => {
          if (error?.status !== 401 && this.session()?.details) {
            this._validation$ = null;
            return of(true);
          }
          this.endSession();
          return of(false);
        }),
        shareReplay(1)
      );
      return this._validation$;
    });
  }

  /** Ends the JWT session. Legacy state is left alone when no JWT session is active. */
  endSession(): void {
    const hadSession = !!this.session();
    this._validation$ = null;
    patchState(this._jwtState, { session: null });
    sessionStorage.removeItem(JWT_SESSION_STORAGE_KEY);
    if (!hadSession) return;
    this.publicLink = null;
    this.apartmentId = null;
    this.publicLinkStartingScreen = PublicLinkStartingScreen.widgets;
    this._publicLink$.next(null);
  }

  can(capability: PublicLinkCapability): boolean {
    return this.capabilities()[capability];
  }

  /**
   * The one question feature code asks about the current visitor: a logged user is never restricted here,
   * a public link visitor (legacy or JWT) is restricted by the link's capabilities.
   */
  allows(capability: PublicLinkCapability): boolean {
    return !this.isPublicLinkModeActive() || this.can(capability);
  }

  /** Who is visiting right now; input for `isDashboardTabAllowed` and similar rules. */
  access(): PublicLinkAccess {
    return { active: this.isPublicLinkModeActive(), jwt: this.isJwtSession(), capabilities: this.capabilities() };
  }

  /** The credential header of the active link: the JWT for new links, the GUID for legacy links. */
  credentialHeader(): PublicLinkCredentialHeader | null {
    const session = this.session();
    if (session) return { name: 'publicLinkToken', value: session.link.token };
    return this.publicLink && this.getGuid() ? { name: 'PublicLink', value: this.publicLink } : null;
  }

  isPublicLinkModeActive(): boolean {
    return !!this._publicLink$.value;
  }

  getStartingScreenPublicLink(): number {
    return this.publicLinkStartingScreen;
  }

  setPublicLinkToLocalStorage(params: PublicLinkData) {
    sessionStorage.setItem('publicLinkParams', JSON.stringify(params));
  }

  isGuidValid$(guid: string, apartmentId: string): Observable<boolean> {
    return this.isPublicLinkGuidValid$(guid, apartmentId);
  }

  getPublicLinkApiBlackList(apiUrl: string) {
    return API_BLACK_LIST.map((apiValue) => `${apiUrl}${apiValue}`);
  }

  private _applyJwtLink(link: ParsedJwtPublicLink): void {
    this.publicLink = link.token;
    this.apartmentId = link.apartmentId;
    this._publicLink$.next(link.token);
  }

  private _persistJwtSession(): void {
    sessionStorage.setItem(JWT_SESSION_STORAGE_KEY, JSON.stringify(this.session()));
  }

  private _restoreJwtSession(): void {
    const stored = sessionStorage.getItem(JWT_SESSION_STORAGE_KEY);
    if (!stored) return;
    try {
      const session: PublicLinkSession = JSON.parse(stored);
      if (session?.link?.kind !== 'jwt' || !session.link.token) throw new Error('invalid session');
      this.startSession(session.link);
      if (session.details) this.setDetails(session.details);
    } catch {
      sessionStorage.removeItem(JWT_SESSION_STORAGE_KEY);
    }
  }

  private isPublicLinkGuidValid$(publicLinkGuid: string, apartmentId: string): Observable<boolean> {
    // using /Access/GetUserFullAccesses as public Link Validator
    if (!apartmentId) return of(false);
    return this._http
      .get<{
        isOwner: boolean;
        roomAccesses: unknown[];
      }>(`${this._apiUrl}/Access/GetUserFullAccesses?apartmentGuid=${apartmentId}&publicLink=${publicLinkGuid}`)
      .pipe(
        map(
          (userFullAccess: { isOwner: boolean; roomAccesses: unknown[] }) =>
            !!userFullAccess && (userFullAccess.roomAccesses.length !== 0 || userFullAccess.isOwner)
        ),
        catchError((error) => {
          console.log(error);
          return of(false);
        })
      );
  }
}

function pickCapabilities(details: PublicLinkCapabilities): PublicLinkCapabilities {
  return {
    canViewDevices: details.canViewDevices,
    canControlDevices: details.canControlDevices,
    canStartScenes: details.canStartScenes,
    canViewEvents: details.canViewEvents,
    canViewTickets: details.canViewTickets,
    canViewInteriorTemplates: details.canViewInteriorTemplates
  };
}

export function b64_to_utf8(value: string): any {
  let object = {};
  try {
    object = JSON.parse(decodeURIComponent(escape(window.atob(value))));
  } catch {}
  return object;
}
